import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, TextField, Button,
  CircularProgress, Alert, Grid, Card, CardContent
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppleIcon from '@mui/icons-material/Apple';
import { createPaymentLink, createPreorder, createSubscriptionLink, formatPrice, PRODUCTS, SUBSCRIPTIONS } from '../services/paymentService';
import { rememberPurchase } from '../utils/adsConversion';
import { annualCalendarYear } from '../utils/calendarAccess';
import {
  getCurrentPricing,
  getCurrentAnnualPricing,
  getNttWeeklyPricing,
  getNttMonthlyPricing,
  getNttAnnualPricing,
  getNtWeeklyPricing,
  getNtMonthlyPricing,
  getNtAnnualPricing
} from '../config/pricing.config';

// 2027 forecast bundles are sold as pre-orders (reserve -> pay any time -> delivered Oct 1)
const FORECAST_BUNDLE_KEY = {
  'Single Rising Sign': 'single',
  'Big Three Bundle': 'three',
  'All 12 Signs Bundle': 'twelve',
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  // a cancelled Year Ahead 2027 payment lands here (the Lambda's cancel_url): send it back to its own page
  useEffect(() => {
    const back = sessionStorage.getItem('yearAheadReturn');
    if (back) { sessionStorage.removeItem('yearAheadReturn'); navigate(back, { replace: true }); }
  }, [navigate]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState(() => sessionStorage.getItem('checkout-return-email') || '');
  const [name, setName] = useState('');
  const [preorderResult, setPreorderResult] = useState(null);
  // calendar discount code (sent with 2027 deep-dive orders); kept in sessionStorage so it survives the login redirect
  const [promoCode, setPromoCode] = useState(() => sessionStorage.getItem('calendarPromoCode') || '');

  // The year an annual calendar bought today is for (from October 1st: next year)
  const annualYear = annualCalendarYear();

  // Get current pricing for display
  const currentCalendarPricing = getCurrentPricing();
  const currentAnnualPricing = getCurrentAnnualPricing();
  const nttWeeklyPricing = getNttWeeklyPricing();
  const nttMonthlyPricing = getNttMonthlyPricing();
  const nttAnnualPricing = getNttAnnualPricing();
  const ntWeeklyPricing = getNtWeeklyPricing();
  const ntMonthlyPricing = getNtMonthlyPricing();
  const ntAnnualPricing = getNtAnnualPricing();
  const [selectedProduct] = useState(() => {
    return sessionStorage.getItem('selectedProduct') || 'Single Rising Sign';
  });
  const [selectedSign] = useState(() => {
    return sessionStorage.getItem('selectedRisingSign') || 'Aries';
  });
  const [selectedSigns] = useState(() => {
    const signs = sessionStorage.getItem('selectedSigns');
    return signs ? JSON.parse(signs) : [];
  });

  // Calculate product and price (with optional discount from sessionStorage)
  const getProductInfo = () => {
    let info;
    if (selectedProduct === 'Monthly Cosmic Personal Events') {
      info = SUBSCRIPTIONS.MONTHLY_CALENDAR_2026;
    } else if (selectedProduct === 'Annual Cosmic Personal Events') {
      info = SUBSCRIPTIONS.ANNUAL_CALENDAR_2026;
    } else if (selectedProduct === 'Weekly Personal Cosmic Activation') {
      info = SUBSCRIPTIONS.NTT_WEEKLY_CALENDAR_2026;
    } else if (selectedProduct === 'Monthly Personal Cosmic Activation') {
      info = SUBSCRIPTIONS.NTT_MONTHLY_CALENDAR_2026;
    } else if (selectedProduct === 'Annual Personal Cosmic Activation') {
      info = SUBSCRIPTIONS.NTT_ANNUAL_CALENDAR_2026;
    } else if (selectedProduct === 'Weekly Personal Transit') {
      info = SUBSCRIPTIONS.NT_WEEKLY_CALENDAR_2026;
    } else if (selectedProduct === 'Monthly Personal Transit') {
      info = SUBSCRIPTIONS.NT_MONTHLY_CALENDAR_2026;
    } else if (selectedProduct === 'Annual Personal Transit') {
      info = SUBSCRIPTIONS.NT_ANNUAL_CALENDAR_2026;
    } else if (selectedProduct === 'Big Three Bundle') {
      info = PRODUCTS.BIG_THREE_BUNDLE;
    } else if (selectedProduct === 'All 12 Signs Bundle') {
      info = PRODUCTS.ALL_TWELVE_SIGNS;
    } else {
      info = PRODUCTS.SINGLE_RISING;
    }
    // Apply discounted price from sessionStorage if available (forecast video progressive discount)
    const discountedPrice = sessionStorage.getItem('discountedPrice');
    if (discountedPrice && !Object.prototype.hasOwnProperty.call(FORECAST_BUNDLE_KEY, selectedProduct)) {
      const discountedCents = Math.round(parseFloat(discountedPrice) * 100);
      if (discountedCents > 0 && discountedCents <= info.amount_cents) {
        return { ...info, amount_cents: discountedCents, originalAmount: info.amount_cents };
      }
    }
    return info;
  };

  // Check if product is a subscription
  const isSubscription = () => {
    return selectedProduct === 'Monthly Cosmic Personal Events' ||
           selectedProduct === 'Annual Cosmic Personal Events' ||
           selectedProduct === 'Weekly Personal Cosmic Activation' ||
           selectedProduct === 'Monthly Personal Cosmic Activation' ||
           selectedProduct === 'Annual Personal Cosmic Activation' ||
           selectedProduct === 'Weekly Personal Transit' ||
           selectedProduct === 'Monthly Personal Transit' ||
           selectedProduct === 'Annual Personal Transit';
  };

  // The calendar discount code works on monthly and yearly calendar subscriptions
  const acceptsCalendarCode = () => isSubscription() && ['month', 'year'].includes(getProductInfo().interval);

  const isForecastBundle = () => Object.prototype.hasOwnProperty.call(FORECAST_BUNDLE_KEY, selectedProduct);

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 2027 forecast bundles are PRE-ORDERS: reserve now, pay any time, delivered Oct 1.
      if (isForecastBundle()) {
        if (!name.trim()) { setError('Please enter your name'); setLoading(false); return; }
        const bundle = FORECAST_BUNDLE_KEY[selectedProduct];
        // signs by bundle: a Big Three pick left in sessionStorage must not leak into a later Single order
        const signs = bundle === 'twelve' ? [] : bundle === 'single' ? [selectedSign] : selectedSigns;
        const res = await createPreorder({ email, name: name.trim(), bundle, signs });
        // the Payment Link returns to /thank-you?order=<orderId>: remember the amount for the Google Ads report
        rememberPurchase(res.orderId, res.price, res.currency);
        setPreorderResult(res);
        setLoading(false);
        return;
      }

      const productInfo = getProductInfo();
      let response;

      // Check if this is a subscription or one-time payment
      if (isSubscription()) {
        // For calendar subscriptions, user needs to be logged in
        // Check if user is logged in to transit calendar
        const token = localStorage.getItem('transit-calendar-token');
        if (!token) {
          // Redirect to login/register with return URL
          sessionStorage.setItem('checkout-return-product', selectedProduct);
          sessionStorage.setItem('checkout-return-email', email);
          window.location.href = `/transit-calendar/login?redirect=${encodeURIComponent('/checkout')}`;
          return;
        }

        // Get user_id from token
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.sub;

        // Create subscription link via Lambda (the calendar code is checked there)
        response = await createSubscriptionLink({
          email: email,
          user_id: userId,
          plan_name: productInfo.name,
          amount_cents: productInfo.amount_cents,
          currency: productInfo.currency,
          interval: productInfo.interval,
          year: productInfo.interval === 'year' ? annualYear : undefined,
          promo_code: acceptsCalendarCode() ? promoCode.trim() : ''
        });
      } else {
        // Create one-time payment link via Lambda
        response = await createPaymentLink({
          product_name: productInfo.name,
          amount_cents: productInfo.amount_cents,
          currency: productInfo.currency,
          customer_email: email,
          metadata: {
            rising_sign: selectedSign,
            product_type: selectedProduct,
            selected_signs: selectedSigns.length > 0 ? selectedSigns.join(', ') : selectedSign
          }
        });
      }

      if (response.success && response.url) {
        // one-time payments return to /thank-you?session_id=<this session>: remember the amount for the
        // Google Ads report (subscriptions return to /dashboard and are not reported)
        if (!isSubscription()) rememberPurchase(response.stripe_session_id, productInfo.amount_cents / 100, productInfo.currency);
        // Redirect directly to Stripe Checkout
        // Stripe automatically shows Apple Pay, Google Pay, and other payment methods
        window.location.href = response.url;
      } else {
        setError(response.error || 'Failed to create checkout session');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      console.error('Checkout error:', err);
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom>
              Complete Your Purchase
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {preorderResult ? (
              <Box>
                <Typography variant="h5" gutterBottom sx={{ color: 'success.main', fontWeight: 700 }}>
                  ✓ You're in!
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Your 2027 deep-dive order is saved{preorderResult.discountPercent ? ` at ${preorderResult.discountPercent}% off` : ''}{preorderResult.price ? ` (${formatPrice(Math.round(Number(preorderResult.price) * 100), preorderResult.currency || 'eur')})` : ''}. We've also emailed your confirmation and payment link to <strong>{email}</strong>.
                </Typography>
                <Typography sx={{ mb: 3 }} color="text.secondary">
                  Complete your payment below and your deep-dive video and interactive maps are emailed to you — {preorderResult.ready === true ? 'usually within minutes, and always within 24 hours' : 'within 24 hours of your payment'}.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  href={preorderResult.payLink}
                  sx={{ py: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  Complete payment →
                </Button>
              </Box>
            ) : (
            <form onSubmit={handleCheckout}>
              {isForecastBundle() && (
                <TextField
                  fullWidth
                  label="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 3 }}
                helperText="You'll receive your access details at this email"
              />
              {acceptsCalendarCode() && (
                <TextField
                  fullWidth
                  label="Calendar discount code (optional)"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value); sessionStorage.setItem('calendarPromoCode', e.target.value); }}
                  sx={{ mb: 3 }}
                  helperText="Got a code with your 2027 Deep Dive? Enter it here and the discount is applied at checkout."
                  inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
                />
              )}

              <Box sx={{ bgcolor: '#12121f', p: 2, borderRadius: 1, mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <LockIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Secure payment powered by Stripe
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Your payment information is encrypted and secure. We never store your card details.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    Supports:
                  </Typography>
                  <AppleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">Apple Pay</Typography>
                  <Typography variant="caption" color="text.secondary">• Google Pay</Typography>
                  <Typography variant="caption" color="text.secondary">• Credit/Debit Cards</Typography>
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !email || (isForecastBundle() && !name)}
                sx={{
                  py: 2,
                  background: loading ? 'grey.400' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>{isForecastBundle() ? 'Reserving...' : 'Loading secure checkout...'}</span>
                  </Box>
                ) : (
                  isForecastBundle() ? 'Continue to payment' : 'Continue to Secure Checkout'
                )}
              </Button>
            </form>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
              {selectedProduct === 'Monthly Cosmic Personal Events' || selectedProduct === 'Annual Cosmic Personal Events' ||
               selectedProduct === 'Weekly Personal Cosmic Activation' || selectedProduct === 'Monthly Personal Cosmic Activation' ||
               selectedProduct === 'Annual Personal Cosmic Activation' ||
               selectedProduct === 'Weekly Personal Transit' || selectedProduct === 'Monthly Personal Transit' ||
               selectedProduct === 'Annual Personal Transit'
                ? 'Cancel anytime • Instant access • No long-term commitment'
                : isForecastBundle()
                  ? '30-day money-back guarantee • Delivered by email • Lifetime access'
                  : '30-day money-back guarantee • Instant access • Lifetime updates'
              }
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Order Summary
              </Typography>

              <Box sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                {selectedProduct === 'Monthly Cosmic Personal Events' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Monthly Cosmic Personal Events
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Personal House Transit-to-Transit Calendar Subscription
                    </Typography>
                  </>
                ) : selectedProduct === 'Annual Cosmic Personal Events' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Annual Cosmic Personal Events - {annualYear}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Personal House Transit-to-Transit Calendar Subscription
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opens today and runs to 31 December {annualYear}
                    </Typography>
                  </>
                ) : selectedProduct === 'Weekly Personal Cosmic Activation' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Weekly Personal Cosmic Activation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Natal-to-Current Aspects Calendar Subscription
                    </Typography>
                  </>
                ) : selectedProduct === 'Monthly Personal Cosmic Activation' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Monthly Personal Cosmic Activation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Natal-to-Current Aspects Calendar Subscription
                    </Typography>
                  </>
                ) : selectedProduct === 'Annual Personal Cosmic Activation' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Annual Personal Cosmic Activation - {annualYear}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Natal-to-Current Aspects Calendar Subscription
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opens today and runs to 31 December {annualYear}
                    </Typography>
                  </>
                ) : selectedProduct === 'Weekly Personal Transit' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Weekly Personal Transit
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Natal-to-Transit Calendar Subscription
                    </Typography>
                  </>
                ) : selectedProduct === 'Monthly Personal Transit' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Monthly Personal Transit
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Natal-to-Transit Calendar Subscription
                    </Typography>
                  </>
                ) : selectedProduct === 'Annual Personal Transit' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Annual Personal Transit - {annualYear}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Natal-to-Transit Calendar Subscription
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opens today and runs to 31 December {annualYear}
                    </Typography>
                  </>
                ) : selectedProduct === 'Big Three Bundle' && selectedSigns.length > 0 ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Big Three Bundle - 3 Complete Forecasts
                    </Typography>
                    {selectedSigns.map((sign, idx) => (
                      <Typography key={idx} variant="body2" color="text.secondary">
                        {idx + 1}. {sign} Rising - Extended Forecast
                      </Typography>
                    ))}
                  </>
                ) : selectedProduct === 'All 12 Signs Bundle' ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      All 12 Signs Bundle - Complete Collection
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All 12 zodiac signs - 2027 Complete Analysis
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      {selectedSign} Rising - Extended Forecast
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      2027 Complete Analysis
                    </Typography>
                  </>
                )}
              </Box>

              <Box sx={{ py: 2 }}>
                {selectedProduct === 'Monthly Cosmic Personal Events' || selectedProduct === 'Annual Cosmic Personal Events' ||
                 selectedProduct === 'Weekly Personal Cosmic Activation' || selectedProduct === 'Monthly Personal Cosmic Activation' ||
                 selectedProduct === 'Annual Personal Cosmic Activation' ||
                 selectedProduct === 'Weekly Personal Transit' || selectedProduct === 'Monthly Personal Transit' ||
                 selectedProduct === 'Annual Personal Transit' ? (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      {selectedProduct === 'Annual Cosmic Personal Events'
                        ? 'Full year personal house transit-to-transit calendar view'
                        : selectedProduct === 'Annual Personal Cosmic Activation'
                        ? 'Full year natal-to-current aspects calendar view'
                        : selectedProduct === 'Weekly Personal Cosmic Activation'
                        ? '7 days of natal-to-current aspects calendar view'
                        : selectedProduct === 'Monthly Personal Cosmic Activation'
                        ? '30 days of natal-to-current aspects calendar view'
                        : selectedProduct === 'Annual Personal Transit'
                        ? 'Full year natal-to-transit aspects calendar view'
                        : selectedProduct === 'Weekly Personal Transit'
                        ? '7 days of natal-to-transit aspects calendar view'
                        : selectedProduct === 'Monthly Personal Transit'
                        ? '30 days of natal-to-transit aspects calendar view'
                        : 'Current month personal house transit-to-transit calendar view'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      {selectedProduct === 'Annual Cosmic Personal Events'
                        ? 'Navigate through all months of the year'
                        : selectedProduct === 'Annual Personal Cosmic Activation'
                        ? 'Navigate through all months of the year'
                        : selectedProduct === 'Weekly Personal Cosmic Activation'
                        ? 'View natal aspects for the entire week'
                        : selectedProduct === 'Monthly Personal Cosmic Activation'
                        ? 'Navigate through all weeks within your 30-day period'
                        : selectedProduct === 'Annual Personal Transit'
                        ? 'Navigate through all months of the year'
                        : selectedProduct === 'Weekly Personal Transit'
                        ? 'View natal transits for the entire week'
                        : selectedProduct === 'Monthly Personal Transit'
                        ? 'Navigate through all weeks within your 30-day period'
                        : 'Navigate through all weeks of the month'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      {selectedProduct.includes('Cosmic Activation')
                        ? 'Detailed aspect interpretations for natal planet transits'
                        : selectedProduct.includes('Personal Transit')
                        ? 'Detailed aspect interpretations for natal-to-transit aspects'
                        : 'Detailed aspect interpretations (as seen on your weekly calendar view)'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      {selectedProduct.includes('Cosmic Activation')
                        ? 'See when current planets aspect your natal chart'
                        : selectedProduct.includes('Personal Transit')
                        ? 'See when transits aspect your natal planets'
                        : 'House positions for all transits (as seen on your weekly calendar view)'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      {selectedProduct.includes('Cosmic Activation')
                        ? 'Exact timing for all natal aspects'
                        : selectedProduct.includes('Personal Transit')
                        ? 'Exact timing for all natal-to-transit aspects'
                        : 'Exact timing for all aspects (as seen on your weekly calendar view)'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      Personalized to your {selectedProduct.includes('Cosmic Activation') || selectedProduct.includes('Personal Transit') ? 'birth chart' : 'natal chart'}
                    </Typography>
                    {(selectedProduct === 'Annual Cosmic Personal Events' || selectedProduct === 'Annual Personal Cosmic Activation' || selectedProduct === 'Annual Personal Transit') && (
                      <Typography variant="body2" gutterBottom>
                        <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        Priority support
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      Almost 2-hour video deep dive
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      House by House Transits & Degrees impacted
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      Month by month timing guidance
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      Monthly transit interactive map
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      House interactive map
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      Key Activation Windows & House activations
                    </Typography>
                  </>
                )}
              </Box>

              <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Tax</Typography>
                  <Typography>{formatPrice(0, getProductInfo().currency)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedProduct === 'Monthly Cosmic Personal Events' ? currentCalendarPricing.checkoutTotal :
                     selectedProduct === 'Annual Cosmic Personal Events' ? currentAnnualPricing.checkoutTotal :
                     selectedProduct === 'Weekly Personal Cosmic Activation' ? nttWeeklyPricing.checkoutTotal :
                     selectedProduct === 'Monthly Personal Cosmic Activation' ? nttMonthlyPricing.checkoutTotal :
                     selectedProduct === 'Annual Personal Cosmic Activation' ? nttAnnualPricing.checkoutTotal :
                     selectedProduct === 'Weekly Personal Transit' ? ntWeeklyPricing.checkoutTotal :
                     selectedProduct === 'Monthly Personal Transit' ? ntMonthlyPricing.checkoutTotal :
                     selectedProduct === 'Annual Personal Transit' ? ntAnnualPricing.checkoutTotal :
                     preorderResult?.price ? formatPrice(Math.round(Number(preorderResult.price) * 100), preorderResult.currency || 'eur') :
                     formatPrice(getProductInfo().amount_cents, getProductInfo().currency)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;